import get from 'lodash/get';
import has from 'lodash/has';
import set from 'lodash/set';
import { DateTime } from 'luxon';
import qs from 'qs';
import type { z, ZodTypeAny } from 'zod';

import { IMAGE_KIND } from 'Components/pages/VehicleDetailsRedesign/VehiclePhotos.const';
import { SELLER_TYPES } from 'Components/VehicleList/VehicleListFilters.consts';
import { logger } from 'Services/logger/logger';
import { Order } from 'Types/generic';
import type { ListType } from 'Types/listType';
import type { SellerType } from 'Types/sellerType';
import { LIST_TYPES } from 'Utilities/consts';
import { VEHICLE_SORT_CONDITIONAL_OPTIONS } from 'Utilities/hooks/vehicleList/useSortOrder/useSortOrder.const';
import { getMakeModelQuery } from 'Utilities/makeModel/makeModelQuery.helpers';

import type { GetVehicleListRequestQuery } from './api/vehicles.types';
import {
	ALLOWED_STATES_FOR_MAKE_MODEL_COUNT,
	INVERSE_FEATURE_TOGGLES,
	QUERY_STRING_OPTIONS,
	RESULTS_PER_PAGE,
	TEST_VEHICLE_ID,
} from './dataProvider.const';
import type { BEToFEFeatureFlagsMapParam, FeatureFlags, ScopedFeatureFlags } from './dataProvider.types';
import { resolveStatesToFetch } from './services.helpers';

export const isTestVehicle = (vehicleId: number): boolean => {
	if (vehicleId !== TEST_VEHICLE_ID) {
		return false;
	}
	// We want to see if this test vehicle still exists in the results.
	// if it does not appear in logs, we can likely remove all related code for filtering it out.
	// Original PR for the test vehicle: https://github.com/motorway/motorway-dealer-webapp/pull/37#discussion_r263738382
	logger.warn({ message: 'Test vehicle removed from results', scope: 'isTestVehicle' });

	return true;
};

// NOTE: This is a legacy param that is used for testing on prod and not to be confused with Motorway Trade.
// It allows us to have vehicles that only specific test users can see on prod.
// Original PR: https://github.com/motorway/motorway-dealer-webapp/pull/460
const resolveExclusiveToDealer = (includeOnlyInternalTestingVehicles?: string | boolean) =>
	includeOnlyInternalTestingVehicles ? 'Trade' : 'None'; // NOTE: here

// Filter by Trade vehicles (listed by Motorway Trade users)
const resolveIsTrade = (sellerType?: SellerType): boolean | undefined => {
	const isSellerTypeCorrectType = sellerType && typeof sellerType === 'string';
	if (!isSellerTypeCorrectType) {
		return undefined;
	}

	if (sellerType === SELLER_TYPES.ALL || sellerType === SELLER_TYPES.FLEET) {
		return undefined;
	}

	return sellerType === SELLER_TYPES.TRADE;
};

const resolveFleetOnly = (sellerType?: SellerType): boolean | undefined => {
	if (sellerType !== SELLER_TYPES.FLEET) {
		return undefined;
	}
	return true;
};

// We want to limit how old the sold vehicle entries we're fetching are
export const generateSoldDateTimestamp = (): Nullable<string> => DateTime.now().minus({ years: 1 }).toISODate();

export const getMakeModelStateFilter = (listType?: ListType): string[] => {
	if (!listType || !ALLOWED_STATES_FOR_MAKE_MODEL_COUNT[listType]) {
		return [];
	}
	return ALLOWED_STATES_FOR_MAKE_MODEL_COUNT[listType];
};

export const getMakeModelCountQuery = (params: GetVehicleListRequestQuery): string => {
	const {
		ageFrom,
		ageTo,
		bodyCategory,
		displayPriceFrom,
		displayPriceTo,
		distanceFromDealerId,
		excludeVehiclesOnFinance,
		exteriorGrade,
		fuel,
		hasEarlyDocuments,
		includeOnlyInternalTestingVehicles,
		isAvailableForCollection,
		isPaidListing,
		listType,
		maxDistance,
		mileageFrom,
		mileageTo,
		numericGrade,
		previousKeepersCountFrom,
		previousKeepersCountTo,
		sellerType,
		serviceHistory,
		transmission,
		vehicleClass,
	} = params;

	const isShortlist = listType === LIST_TYPES.shortlist;

	const state = getMakeModelStateFilter(listType);

	if (!state) {
		return '';
	}

	const isTrade = resolveIsTrade(sellerType);
	const fleetOnly = resolveFleetOnly(sellerType);

	const apiParams = {
		ageFrom,
		ageTo,
		bodyCategory,
		countBy: ['makes', 'models'],
		displayPriceFrom,
		displayPriceTo,
		distanceFromDealerId,
		excludeVehiclesOnFinance,
		exclusiveToDealer: resolveExclusiveToDealer(includeOnlyInternalTestingVehicles), // NOTE: here
		fuel,
		...(isShortlist ? { forSale: true } : {}),
		grade: exteriorGrade,
		hasEarlyDocuments,
		isAvailableForCollection,
		isPaidListing,
		maxDistance,
		mileageFrom,
		mileageTo,
		numericGrade,
		previousKeepersCountFrom,
		previousKeepersCountTo,
		scope: 'live-sale',
		serviceHistory,
		shortlisted: isShortlist,
		state,
		transmission,
		vehicleClass,
		...(fleetOnly ? { fleetOnly } : {}),
		...(isTrade ? { isTrade } : {}),
	};

	return `${qs.stringify(apiParams, QUERY_STRING_OPTIONS)}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any, local-rules/function-naming
export const redact = (obj: Record<any, any>, ...keys: string[]): Record<any, any> => {
	const logs = { ...obj };

	keys.forEach((key) => {
		if (key.includes('config.data.') && has(logs, 'config.data')) {
			try {
				const shouldParseData = typeof get(logs, 'config.data') === 'string';
				const parsedData = shouldParseData ? JSON.parse(get(logs, 'config.data')) : get(logs, 'config.data');
				const nestedDataPath = key.split('config.data.')[1];
				const valueToRedact = get(parsedData, nestedDataPath);
				if (valueToRedact) {
					set(parsedData, nestedDataPath, '[REDACTED]');
					set(logs, 'config.data', shouldParseData ? JSON.stringify(parsedData) : parsedData);
				}
				// eslint-disable-next-line no-empty
			} catch {}
			return;
		}

		if (get(logs, key)) {
			set(logs, key, '[REDACTED]');
		}
	});

	return logs;
};

export const extractFEFeatureFlags = (
	featureFlags: ScopedFeatureFlags,
	BEToFEFeatureFlagsMap: BEToFEFeatureFlagsMapParam,
): FeatureFlags => {
	const mappingEntries = Object.entries(BEToFEFeatureFlagsMap);
	try {
		return mappingEntries.reduce((acc, [beToggleName, { feToggleName, scope }]) => {
			// Scope determines whether we take dealership toggle value or user toggle value
			const scopedFlagValue = featureFlags[scope]?.[beToggleName];
			// Some toggles fetched from LD as true when we want to set them to false on the FE and vice versa
			const shouldInverseBEValue = INVERSE_FEATURE_TOGGLES.includes(feToggleName);

			acc[feToggleName] = shouldInverseBEValue ? !scopedFlagValue : Boolean(scopedFlagValue);
			return acc;
		}, {} as FeatureFlags);
	} catch (e) {
		const error = e instanceof Error ? e : new Error('There was an error extracting feature flags');
		logger.error({ error, message: error.message, scope: 'dataProvider.helpers extractFEFeatureFlags' });
		return {};
	}
};
// =====================================================================================================================
// TODO: Use this in the make/model count query function.
// This uses a lot of similar logic, but just picks some of the values from the params object.
// Therefore it makes sense to combine them.
// =====================================================================================================================

export const getVehicleListQuery = (params: GetVehicleListRequestQuery): string => {
	const {
		ageFrom,
		ageTo,
		displayPriceFrom,
		displayPriceTo,
		distanceFromDealerId,
		excludeVehiclesOnFinance,
		exteriorGrade,
		fuel,
		hasEarlyDocuments,
		includeOnlyInternalTestingVehicles,
		includeSold,
		isAvailableForCollection,
		isPaidListing,
		listType,
		make: makeModels,
		maxDistance,
		mileageFrom,
		mileageTo,
		numericGrade,
		order,
		page,
		partners,
		previousKeepersCountFrom,
		previousKeepersCountTo,
		recommendedSortOrderVersion,
		sellerType,
		serviceHistory,
		sort,
		timeId,
		transmission,
		useLiveSaleIndex,
		vehicleClass,
	} = params;

	const startAt = page === undefined ? undefined : Number(page) * RESULTS_PER_PAGE;
	const endAt = page === undefined ? undefined : (startAt ?? 0) + RESULTS_PER_PAGE;
	const state = resolveStatesToFetch(params);
	const isTrade = resolveIsTrade(sellerType);
	const fleetOnly = resolveFleetOnly(sellerType);
	const isShortlist = listType === LIST_TYPES.shortlist;
	const isAuction = listType === LIST_TYPES.auction;

	const apiParams = {
		_end: endAt,
		_include: ['images', 'bids', 'model'],
		_order: order ?? Order.Descending,
		_sort: sort,
		_start: startAt,
		ageFrom,
		ageTo,
		displayPriceFrom,
		displayPriceTo,
		distanceFromDealerId,
		excludeVehiclesOnFinance,
		exclusiveToDealer: resolveExclusiveToDealer(includeOnlyInternalTestingVehicles),
		grade: exteriorGrade,
		hasEarlyDocuments,
		imageKind: IMAGE_KIND.exteriorFrontDriver, // Only fetch main vehicle image as others not required on vehicle list
		isAvailableForCollection,
		isPaidListing,
		maxDistance,
		mileageFrom,
		mileageTo,
		numericGrade,
		...(partners?.length ? { partners } : {}),
		previousKeepersCountFrom,
		previousKeepersCountTo,
		serviceHistory,
		shortlisted: isShortlist,
		spec: {
			fuel,
			transmission,
			vehicleClass,
		},
		state,
		...(isShortlist ? { forSale: true } : {}),
		...(isAuction && timeId ? { timeId } : {}),
		...(fleetOnly ? { fleetOnly } : {}),
		...(isTrade !== undefined ? { isTrade } : {}),
		...(sort === VEHICLE_SORT_CONDITIONAL_OPTIONS.recommended ? { recommendedSortOrderVersion } : {}),
		...(includeSold ? { soldDateFrom: generateSoldDateTimestamp(), useSoldDateFromOrNull: true } : {}),
		...(useLiveSaleIndex ? { scope: 'live-sale' } : {}),
	};

	const makeModelQuery = getMakeModelQuery(makeModels);

	return state ? `${qs.stringify(apiParams, QUERY_STRING_OPTIONS)}${makeModelQuery}` : '';
};

export const validateAndEncodeValue = <T extends ZodTypeAny>({
	input,
	schema,
}: {
	input: unknown;
	schema: T;
}): string => {
	const sanitisedInput: z.infer<T> = schema.parse(input);
	const encodedSanitisedInput = encodeURIComponent(sanitisedInput);

	return encodedSanitisedInput;
};

export const validateAndEncodeValueSafe = <T extends ZodTypeAny>({
	input,
	schema,
}: {
	input: unknown;
	schema: T;
}): { error?: z.ZodError; success: Boolean; value?: unknown } => {
	try {
		const sanitisedInput: z.infer<T> = schema.parse(input);
		const encodedSanitisedInput = encodeURIComponent(sanitisedInput);
		return { success: true, value: encodedSanitisedInput };
	} catch (err: unknown) {
		return { error: err as z.ZodError, success: false };
	}
};
